import { supabase } from "../supabase-client";
import type { Database } from "../types/database";
import { withProfiles } from "./client_profiles";

export type Case = Database["public"]["Tables"]["cases"]["Row"];
export type CaseInsert = Database["public"]["Tables"]["cases"]["Insert"];
export type CaseUpdate = Database["public"]["Tables"]["cases"]["Update"];

export interface CaseWithDetails extends Case {
  lawyer_name?: string;
  lawyer_email?: string;
  client_name?: string;
  client_email?: string;
  document_count?: number;
  event_count?: number;
}

export interface CaseStats {
  total: number;
  pending: number;
  active: number;
  closed: number;
  archived: number;
  by_practice_area: Record<string, number>;
}

/**
 * Create a new case
 */
export async function createCase(
  caseData: Omit<CaseInsert, "id" | "created_at" | "updated_at" | "case_number">
) {
  // Check limit before creating case
  if (caseData.lawyer_id) {
    const { checkFeatureLimit } = await import('./subscriptions');
    const canCreate = await checkFeatureLimit(caseData.lawyer_id, 'case');
    
    if (!canCreate) {
      throw new Error('Case creation limit reached. Please upgrade your plan or purchase credits.');
    }
  }

  // case_number will be auto-generated by database trigger
  const { data, error } = await supabase
    .from("cases")
    .insert({
      ...caseData,
      case_number: "", // Empty string - trigger will generate it
    } as CaseInsert)
    .select()
    .single();

  if (error) throw error;

  // Increment usage tracking (and use credits if needed)
  if (caseData.lawyer_id) {
    try {
      const { incrementUsage } = await import('./subscriptions');
      await incrementUsage(caseData.lawyer_id, 'case', 1);
    } catch (usageError) {
      console.error("Error incrementing usage:", usageError);
      // Don't fail case creation if usage tracking fails
    }
  }

  // Create initial event for case creation
  if (data && caseData.lawyer_id) {
    try {
      await createCaseEvent(data.id, {
        event_type: "case_created",
        event_title: "Caso creado",
        event_description: `Caso "${data.title}" fue creado`,
        triggered_by: caseData.lawyer_id,
        event_data: {
          case_number: data.case_number,
          practice_area: data.practice_area,
        },
      });
    } catch (eventError) {
      // Don't fail case creation if event creation fails
      console.error("Error creating case event:", eventError);
    }
  }

  return data;
}

/**
 * Get cases by lawyer ID
 */
export async function getCasesByLawyer(
  lawyerId: string,
  options?: {
    status?: string;
    practice_area?: string;
    limit?: number;
    offset?: number;
  }
) {
  let query = supabase
    .from("cases")
    .select(
      `
      *,
      client_profiles(
        id,
        client_type,
        phone,
        full_name,
        email,
        country
      )
    `
    )
    .eq("lawyer_id", lawyerId);

  if (options?.status) {
    query = query.eq("status", options.status);
  }

  if (options?.practice_area) {
    query = query.eq("practice_area", options.practice_area);
  }

  query = query.order("created_at", { ascending: false });

  if (options?.limit) {
    query = query.limit(options.limit);
  }

  if (options?.offset) {
    query = query.range(
      options.offset,
      options.offset + (options.limit || 10) - 1
    );
  }

  const { data, error } = await query;

  if (error) throw error;
  return (data || []).map((c) => ({
    ...c,
    client_profiles: c.client_profiles ? withProfiles(c.client_profiles) : null,
  }));
}

/**
 * Get cases by lawyer with advanced filters
 */
export async function getCasesByLawyerWithFilters(
  lawyerId: string,
  filters?: {
    status?: "pending" | "active" | "closed" | "archived" | "all";
    practice_area?: string;
    priority?: string;
    search?: string;
  }
) {
  let query = supabase
    .from("cases")
    .select(
      `
      *,
      client_profiles(
        id,
        client_type,
        phone,
        full_name,
        email,
        country
      )
    `
    )
    .eq("lawyer_id", lawyerId);

  // Apply status filter
  if (filters?.status && filters.status !== "all") {
    query = query.eq("status", filters.status);
  }

  // Apply practice area filter
  if (filters?.practice_area) {
    query = query.eq("practice_area", filters.practice_area);
  }

  // Apply priority filter
  if (filters?.priority) {
    query = query.eq("priority", filters.priority);
  }

  const { data, error } = await query.order("created_at", {
    ascending: false,
  });

  if (error) throw error;

  const mapped = (data || []).map((c) => ({
    ...c,
    client_profiles: c.client_profiles ? withProfiles(c.client_profiles) : null,
  }));

  // Apply search filter on client side (Supabase doesn't support or with joins well)
  if (filters?.search && mapped.length > 0) {
    const searchLower = filters.search.toLowerCase();
    return mapped.filter((case_) => {
      const title = case_.title?.toLowerCase() || "";
      const caseNumber = case_.case_number?.toLowerCase() || "";
      const description = case_.description?.toLowerCase() || "";
      const clientName =
        case_.client_profiles?.profiles?.full_name?.toLowerCase() || "";
      const clientEmail =
        case_.client_profiles?.profiles?.email?.toLowerCase() || "";

      return (
        title.includes(searchLower) ||
        caseNumber.includes(searchLower) ||
        description.includes(searchLower) ||
        clientName.includes(searchLower) ||
        clientEmail.includes(searchLower)
      );
    });
  }

  return mapped;
}

/**
 * Get cases by client ID
 */
export async function getCasesByClient(
  clientId: string,
  options?: {
    status?: string;
    limit?: number;
  }
) {
  let query = supabase
    .from("cases")
    .select(
      `
      *,
      lawyer_profiles(
        id,
        university,
        years_of_experience,
        practice_areas,
        profiles(
          full_name,
          email,
          profile_image_url
        )
      )
    `
    )
    .eq("client_id", clientId);

  if (options?.status) {
    query = query.eq("status", options.status);
  }

  query = query.order("created_at", { ascending: false });

  if (options?.limit) {
    query = query.limit(options.limit);
  }

  const { data, error } = await query;

  if (error) throw error;
  return data;
}

/**
 * Get case by ID with full details
 */
export async function getCaseDetails(caseId: string) {
  const { data, error } = await supabase
    .from("cases")
    .select(
      `
      *,
      lawyer_profiles(
        id,
        bar_number,
        university,
        years_of_experience,
        practice_areas,
        phone,
        profiles(
          full_name,
          email,
          profile_image_url,
          country
        )
      ),
      client_profiles(
        id,
        client_type,
        phone,
        full_name,
        email,
        country
      )
    `
    )
    .eq("id", caseId)
    .single();

  if (error) throw error;
  if (!data) return null;
  return {
    ...data,
    client_profiles: data.client_profiles
      ? withProfiles(data.client_profiles)
      : null,
  };
}

/**
 * Update case status
 */
export async function updateCaseStatus(
  caseId: string,
  status: "pending" | "active" | "closed" | "archived",
  triggeredBy?: string
) {
  // Get current case to check old status
  const { data: currentCase } = await supabase
    .from("cases")
    .select("status, lawyer_id")
    .eq("id", caseId)
    .single();

  const updateData: CaseUpdate = {
    status,
  };

  // Auto-set dates based on status
  if (status === "active") {
    updateData.started_at = new Date().toISOString();
  } else if (status === "closed") {
    updateData.closed_at = new Date().toISOString();
  }

  const { data, error } = await supabase
    .from("cases")
    .update(updateData)
    .eq("id", caseId)
    .select()
    .single();

  if (error) throw error;

  // Create event for status change (if status actually changed)
  if (data && currentCase && currentCase.status !== status) {
    try {
      const userId = triggeredBy || currentCase.lawyer_id;
      if (userId) {
        await createCaseEvent(caseId, {
          event_type: "status_change",
          event_title: "Estado del caso actualizado",
          event_description: `Estado cambió de ${currentCase.status} a ${status}`,
          triggered_by: userId,
          event_data: {
            old_status: currentCase.status,
            new_status: status,
          },
        });
      }
    } catch (eventError) {
      // Don't fail status update if event creation fails
      console.error("Error creating status change event:", eventError);
    }
  }

  return data;
}

/**
 * Update case
 */
export async function updateCase(caseId: string, updates: CaseUpdate) {
  const { data, error } = await supabase
    .from("cases")
    .update(updates)
    .eq("id", caseId)
    .select()
    .single();

  if (error) throw error;
  return data;
}

/**
 * Delete case
 */
export async function deleteCase(caseId: string) {
  const { error } = await supabase.from("cases").delete().eq("id", caseId);

  if (error) throw error;
}

/**
 * Get case statistics for a lawyer
 */
export async function getCaseStats(lawyerId: string): Promise<CaseStats> {
  const { data, error } = await supabase
    .from("cases")
    .select("status, practice_area")
    .eq("lawyer_id", lawyerId);

  if (error) throw error;

  const stats: CaseStats = {
    total: data.length,
    pending: 0,
    active: 0,
    closed: 0,
    archived: 0,
    by_practice_area: {},
  };

  data.forEach((case_) => {
    // Count by status
    if (case_.status === "pending") stats.pending++;
    else if (case_.status === "active") stats.active++;
    else if (case_.status === "closed") stats.closed++;
    else if (case_.status === "archived") stats.archived++;

    // Count by practice area
    if (case_.practice_area) {
      stats.by_practice_area[case_.practice_area] =
        (stats.by_practice_area[case_.practice_area] || 0) + 1;
    }
  });

  return stats;
}

/**
 * Get active cases count for a lawyer
 */
export async function getActiveCasesCount(lawyerId: string): Promise<number> {
  const { count, error } = await supabase
    .from("cases")
    .select("*", { count: "exact", head: true })
    .eq("lawyer_id", lawyerId)
    .in("status", ["pending", "active"]);

  if (error) throw error;
  return count || 0;
}

/**
 * Get case statistics for a client
 */
export async function getCaseStatsForClient(
  clientId: string
): Promise<CaseStats> {
  const { data, error } = await supabase
    .from("cases")
    .select("status, practice_area")
    .eq("client_id", clientId);

  if (error) throw error;

  const stats: CaseStats = {
    total: data.length,
    pending: 0,
    active: 0,
    closed: 0,
    archived: 0,
    by_practice_area: {},
  };

  data.forEach((case_) => {
    // Count by status
    if (case_.status === "pending") stats.pending++;
    else if (case_.status === "active") stats.active++;
    else if (case_.status === "closed") stats.closed++;
    else if (case_.status === "archived") stats.archived++;

    // Count by practice area
    if (case_.practice_area) {
      stats.by_practice_area[case_.practice_area] =
        (stats.by_practice_area[case_.practice_area] || 0) + 1;
    }
  });

  return stats;
}

/**
 * Search cases
 */
export async function searchCases(
  lawyerId: string,
  searchTerm: string,
  options?: {
    status?: string;
    practice_area?: string;
  }
) {
  let query = supabase
    .from("cases")
    .select(
      `
      *,
      client_profiles!inner(
        id,
        client_type,
        phone,
        full_name,
        email,
        country
      )
    `
    )
    .eq("lawyer_id", lawyerId);

  if (options?.status) {
    query = query.eq("status", options.status);
  }

  if (options?.practice_area) {
    query = query.eq("practice_area", options.practice_area);
  }

  // Search in title, case_number, or description
  query = query.or(
    `title.ilike.%${searchTerm}%,case_number.ilike.%${searchTerm}%,description.ilike.%${searchTerm}%`
  );

  const { data, error } = await query.order("created_at", { ascending: false });

  if (error) throw error;
  return (data || []).map((c) => ({
    ...c,
    client_profiles: c.client_profiles ? withProfiles(c.client_profiles) : null,
  }));
}

/**
 * Add note to case
 */
export async function addCaseNote(
  caseId: string,
  note: {
    content: string;
    author_id: string;
    author_name: string;
  }
) {
  // Get current notes
  const { data: currentCase, error: fetchError } = await supabase
    .from("cases")
    .select("notes")
    .eq("id", caseId)
    .single();

  if (fetchError) throw fetchError;

  const currentNotes = (currentCase.notes as any[]) || [];
  const newNote = {
    ...note,
    timestamp: new Date().toISOString(),
    id: crypto.randomUUID(),
  };

  const updatedNotes = [...currentNotes, newNote];

  const { data, error } = await supabase
    .from("cases")
    .update({ notes: updatedNotes })
    .eq("id", caseId)
    .select()
    .single();

  if (error) throw error;

  // Create event for note addition
  try {
    await createCaseEvent(caseId, {
      event_type: "note_added",
      event_title: "Nota agregada",
      event_description: `Nota agregada por ${note.author_name}`,
      triggered_by: note.author_id,
      event_data: {
        note_id: newNote.id,
        note_preview: note.content.substring(0, 100),
      },
    });
  } catch (eventError) {
    // Don't fail note addition if event creation fails
    console.error("Error creating note event:", eventError);
  }

  return data;
}

/**
 * Get case documents
 */
export async function getCaseDocuments(caseId: string) {
  const { data, error } = await supabase
    .from("case_documents")
    .select(
      `
      *,
      documents!inner(
        id,
        title,
        document_type,
        country,
        content,
        created_at
      ),
      profiles!case_documents_uploaded_by_fkey(
        full_name,
        email
      )
    `
    )
    .eq("case_id", caseId)
    .order("created_at", { ascending: false });

  if (error) throw error;
  return data;
}

/**
 * Add document to case
 */
export async function addDocumentToCase(
  caseId: string,
  documentId: string,
  uploadedBy: string,
  purpose?: string
) {
  // Get document title for event
  const { data: document } = await supabase
    .from("documents")
    .select("title")
    .eq("id", documentId)
    .single();

  const { data, error } = await supabase
    .from("case_documents")
    .insert({
      case_id: caseId,
      document_id: documentId,
      uploaded_by: uploadedBy,
      purpose,
    })
    .select()
    .single();

  if (error) throw error;

  // Create event for document addition
  try {
    await createCaseEvent(caseId, {
      event_type: "document_added",
      event_title: "Documento agregado",
      event_description: `Documento "${document?.title || "N/A"}" fue agregado al caso`,
      triggered_by: uploadedBy,
      event_data: {
        document_id: documentId,
        document_title: document?.title,
        purpose,
      },
    });
  } catch (eventError) {
    // Don't fail document addition if event creation fails
    console.error("Error creating document event:", eventError);
  }

  return data;
}

/**
 * Get case events/timeline
 */
export async function getCaseEvents(caseId: string) {
  const { data, error } = await supabase
    .from("case_events")
    .select(
      `
      *,
      profiles!case_events_triggered_by_fkey(
        full_name,
        profile_image_url
      )
    `
    )
    .eq("case_id", caseId)
    .order("event_date", { ascending: false });

  if (error) throw error;
  return data;
}

/**
 * Create case event
 */
export async function createCaseEvent(
  caseId: string,
  eventData: {
    event_type: string;
    event_title: string;
    event_description?: string;
    triggered_by: string;
    event_data?: Record<string, any>;
  }
) {
  const { data, error } = await supabase
    .from("case_events")
    .insert({
      case_id: caseId,
      ...eventData,
    })
    .select()
    .single();

  if (error) throw error;
  return data;
}
